/**
 * Report Headers Spec — a single-source-of-truth registry of the column
 * headers each parser on the backend expects for every (Ad Product, Report
 * Type) combination the Upload Center supports.
 *
 * Structure of one spec entry:
 *   {
 *     description: "Short human-facing overview of what this report is",
 *     source: "Where the user typically exports this file from",
 *     headers: [
 *       {
 *         name: "Campaign Name",   // exact header text expected in the source
 *         required: true,          // whether the parser strictly needs this column
 *         description: "…",        // short explainer for the header
 *         example: "Wellcore_WP_PLA",
 *       },
 *       ...
 *     ]
 *   }
 *
 * Lookup order (see `getReportHeadersSpec` below):
 *   1) Exact match on `${channel}::${adType}::${adProduct}::${reportType}`
 *   2) Fallback on `${adProduct}::${reportType}` (many products share the
 *      same parser across channels — Sponsored Products under Amazon vs
 *      Zepto, for example, still surface via their own overrides below).
 */

const K = (channel, adType, adProduct, reportType) =>
  [channel, adType, adProduct, reportType].map(s => (s || '').trim()).join('::');

// ---------- Common header building blocks ----------
const H = (name, required, description, example) => ({ name, required, description, example });

// ---------- Registry (keyed by ${channel}::${adType}::${adProduct}::${reportType}) ----------
const SPECS = {
  // ============== FLIPKART · FLIPKART ADS · PLA ==============
  [K('Flipkart', 'Flipkart Ads', 'PLA', 'Consolidated Daily Report (Seller Dashboard)')]: {
    description: 'Daily campaign-level performance export from the Flipkart Seller Dashboard for Product Listing Ads (PLA).',
    source: 'Flipkart Seller Dashboard → Ads → PLA → Reports → Download',
    headers: [
      H('Campaign Name', true, 'Full campaign name as configured in Seller Dashboard.', 'Wellcore_WP_PLA'),
      H('Campaign ID', false, 'Numeric internal ID assigned by Flipkart.', '48219301'),
      H('Date', true, 'Row date (YYYY-MM-DD or DD-MM-YYYY).', '2026-06-01'),
      H('Views', false, 'Impressions served that day.', '12500'),
      H('Clicks', false, 'Ad clicks that day.', '340'),
      H('Ad Spend', true, 'Spend in INR (pre-GST).', '1250.50'),
      H('Total Converted Units', false, 'Attributed units sold.', '18'),
      H('Total Revenue', false, 'Attributed revenue in INR.', '9800.00'),
      H('ROI', false, 'Auto-calculated if omitted (Revenue / Spend).', '7.84'),
    ],
  },
  [K('Flipkart', 'Flipkart Ads', 'PLA', 'Consolidated Daily Report (Ads Portal)')]: {
    description: 'Daily ad-group-level performance export from the Flipkart Ads Portal (newer format with Ad Group column).',
    source: 'Flipkart Ads Portal → Reports → PLA → Consolidated Daily Report',
    headers: [
      H('Campaign Name', true, 'Full campaign name.', 'Wellcore_WP_PLA'),
      H('Campaign ID', false, 'Numeric internal ID.', '48219301'),
      H('Adgroup Name', true, 'Ad group inside the campaign.', 'WP_Broad_KW'),
      H('Date', true, 'Row date (YYYY-MM-DD or DD-MM-YYYY).', '2026-06-01'),
      H('Views', false, 'Impressions.', '12500'),
      H('Clicks', false, 'Clicks.', '340'),
      H('CTR', false, 'Auto-recalculated if omitted.', '2.72%'),
      H('CVR', false, 'Auto-recalculated if omitted.', '5.29%'),
      H('Ad Spend', true, 'Spend (INR, pre-GST).', '1250.50'),
      H('Units Sold (Direct)', false, 'Direct-click attributed units.', '12'),
      H('Units Sold (Indirect)', false, 'View-through attributed units.', '6'),
      H('Direct Revenue', false, 'Direct-click revenue (INR).', '6500.00'),
      H('Indirect Revenue', false, 'View-through revenue (INR).', '3300.00'),
      H('ROI (Direct)', false, 'Reported value (or auto).', '5.20'),
      H('ROI (Indirect)', false, 'Reported value (or auto).', '2.64'),
    ],
  },

  // ============== FLIPKART · FLIPKART ADS · PCA ==============
  [K('Flipkart', 'Flipkart Ads', 'PCA', 'Consolidated Daily Report')]: {
    description: 'Product Category Ads (PCA) daily consolidated report from Flipkart.',
    source: 'Flipkart Ads Portal → Reports → PCA → Consolidated Daily Report',
    headers: [
      H('campaign_name', true, 'Campaign name.', 'Wellcore_CrtnHF2_PCA'),
      H('campaign_id', false, 'Internal ID.', '58120933'),
      H('ad_group_name', true, 'Ad group inside the campaign.', 'AllBK'),
      H('date', true, 'Row date.', '2026-06-01'),
      H('banner_group_spend', true, 'Spend (INR, pre-GST).', '2150.75'),
      H('views', false, 'Impressions.', '18400'),
      H('clicks', false, 'Clicks.', '520'),
      H('ctr', false, 'Auto if omitted.', '2.83%'),
      H('cvr', false, 'Auto if omitted.', '6.15%'),
      H('direct_units', false, 'Direct units sold.', '20'),
      H('indirect_units', false, 'Indirect / view-through units.', '12'),
      H('direct_revenue', false, 'Direct revenue (INR).', '10500.00'),
      H('indirect_revenue', false, 'Indirect revenue (INR).', '5400.00'),
    ],
  },

  // ============== FLIPKART · FLIPKART ADS · Brand Ads ==============
  [K('Flipkart', 'Flipkart Ads', 'Brand Ads', 'Consolidated Report')]: {
    description: 'Flipkart Brand Ads (BGM / TGBS / PP Slot) consolidated performance.',
    source: 'Flipkart Ads Portal → Reports → Brand Ads → Consolidated Report',
    headers: [
      H('campaign_name', true, 'Campaign name.', '188080_BGM-HC_Welcore_Jan1_Jan31_Wellcore_TGBS'),
      H('campaign_id', false, 'Internal ID.', '188080'),
      H('ad_group_name', false, 'Ad group inside the campaign.', 'TGBS'),
      H('date', true, 'Row date.', '2026-06-01'),
      H('campaign_spend', true, 'Spend (INR, pre-GST).', '15000.00'),
      H('sum(views)', false, 'Aggregated impressions.', '250000'),
      H('sum(clicks)', false, 'Aggregated clicks.', '5000'),
      H('ctr_ba', false, 'CTR (auto if omitted).', '2.00%'),
      H('cvr_ba', false, 'CVR (auto if omitted).', '3.50%'),
      H('units_direct_click', false, 'Direct-click units.', '100'),
      H('units_indirect_click', false, 'Indirect-click units.', '35'),
      H('units_direct_view', false, 'Direct-view units.', '50'),
      H('units_indirect_view', false, 'Indirect-view units.', '25'),
      H('revenue_direct_click', false, 'Direct-click revenue (INR).', '55000.00'),
      H('revenue_indirect_click', false, 'Indirect-click revenue (INR).', '20000.00'),
      H('revenue_direct_view', false, 'Direct-view revenue (INR).', '25000.00'),
      H('revenue_indirect_view', false, 'Indirect-view revenue (INR).', '10000.00'),
      H('campaign_roi', false, 'ROI (auto if omitted).', '7.33'),
    ],
  },

  // ============== FLIPKART · OFF PLATFORM ADS ==============
  [K('Flipkart', 'Off Platform Ads', 'Telegram Ads', 'Consolidated Report')]: {
    description: 'Off-platform Telegram / Whatsapp broadcast performance export. Each row = one post/broadcast on a Telegram group or Whatsapp channel. The parser also auto-detects an Amazon variant schema (Channel | Brand | Product | Spends | Date) if those three columns are present instead.',
    source: 'DealBee Deals / Deals Velocity dashboard → Reports → Export CSV',
    headers: [
      H('Date', true, 'Broadcast date (any common date format — parser normalises to YYYY-MM-DD).', '06-06-2026'),
      H('Time', false, 'Broadcast time (HH:MM). Metadata only.', '19:30'),
      H('Brand', true, 'Brand name — used as the row brand.', 'Wellcore'),
      H('SKU ID', true, 'Product SKU / feed item ID.', 'DYN-PWO-30S'),
      H('SKU Name', false, 'Product name (used for display; header may be blank in source files → parser falls back to positional column 5).', 'Dynamite Preworkout — 30 Servings'),
      H('Telegram Group', false, 'Broadcast destination — Telegram group or Whatsapp channel name (header may be blank in source files → parser falls back to positional column 6). Groups containing "whatsapp" auto-tag Post Type = "Whatsapp".', 'DealBee Deals'),
      H('Post type', false, 'Post format (Text / Image / Video / Whatsapp). Auto-derived from group name when blank.', 'Text'),
      H('NOP', false, 'Number of posts (typically 1 per row). Metadata only.', '1'),
      H('Charges', true, 'Amount spent (INR, pre-GST). Also accepts values with ₹ symbol / commas.', '2500.00'),
      H('Inclusive of GST', false, 'Post-GST spend (INR). Auto-computed as Charges × 1.18 if omitted.', '2950.00'),
    ],
  },
  [K('Flipkart', 'Off Platform Ads', 'Google Ads', 'Product Report')]: {
    description: 'Google Ads Product Report — cumulative for the date range picked at upload. Spend + revenue are distributed evenly across every day in the range. Brand detection scans the FULL product title (not just first word) via a priority list so sub-brands (Osmo, Ketofy, Okami, Dynamite, YouWeFit, Zero Sugar) always beat parent brands (Wellcore, Wellversed). Column names are UPPER_SNAKE_CASE.',
    source: 'Google Ads → Reports → Predefined → Shopping → Product → Download CSV (or the internal Merchant-Center dump)',
    headers: [
      H('PRODUCT_ITEM_ID', true, 'Merchant-Center feed item ID / SKU. Also accepts "ITEM_ID".', 'WC-PRE-001'),
      H('PRODUCT_TITLE', true, 'Product title — used for brand detection (full-title scan). Also accepts "TITLE".', 'Wellcore Whey Protein 1kg — Chocolate'),
      H('CAMPAIGN_NAME', false, 'Optional campaign name — used as row campaign when present. Also accepts "CAMPAIGN". Falls back to "{Brand}_GGL" or "Google_Product_Ads" when blank.', 'Wellcore_Shopping_All'),
      H('CLICKS', false, 'Clicks.', '85'),
      H('VIEWS', false, 'Impressions. Also accepts "IMPRESSIONS".', '3200'),
      H('UNITS_SOLD', false, 'Units sold. Also accepts "UNITS".', '4'),
      H('SALES', false, 'Attributed revenue (INR). Also accepts "REVENUE".', '9500.00'),
      H('CTR', false, 'CTR (%). Auto-recomputed if omitted.', '2.66%'),
      H('CVR', false, 'CVR (%). Auto-recomputed if omitted.', '4.71%'),
      H('COST', true, 'Spend (INR, pre-GST). Also accepts "SPEND" / "SPENDS".', '1500.00'),
      H('CPC', false, 'Cost per click.', '17.65'),
      H('ROAS', false, 'Native ROAS. Also accepts "ROI". Auto if omitted.', '6.33'),
    ],
  },
  [K('Flipkart', 'Off Platform Ads', 'Meta Ads by FK', 'Consolidated Report')]: {
    description: 'Meta Ads campaigns run BY Flipkart on your behalf. Each campaign name carries an inline date range (…_May12_May31_…) that the parser auto-extracts to backfill the row date. Files have no per-row date column — the upload endpoint stamps `date` from the form\'s date_range_start when the campaign-name pattern is missing.',
    source: 'Flipkart 3P Performance dashboard → Meta by FK → Export CSV',
    headers: [
      H('Campaign name', true, 'Campaign name — SHOULD contain the inline date range (Mon<day>_Mon<day>) for auto-date backfill. Parser also accepts "Campaign".', '207392_3P-PPA_Wellversed_May12_May31_Dynamite_Facebook'),
      H('Ad set name', false, 'Ad set inside the campaign. Also accepts "Ad set".', 'DVC_ATC_Broad'),
      H('Reach', false, 'Unique users reached.', '95000'),
      H('Impressions', false, 'Impressions.', '250000'),
      H('Clicks (all)', false, 'All clicks (including outbound). Also accepts "Clicks".', '4200'),
      H('CTR (link click-through rate)', false, 'Link CTR — accepts either a decimal fraction (0.0094) or a percentage. Also accepts "CTR".', '1.68%'),
      H('Amount Spents', true, 'Spend (INR, pre-GST). Also accepts "Amount Spent" / "spend".', '25000.00'),
      H('Adds to cart with shared items', false, 'ATC events. Also accepts "atc".', '180'),
      H('Purchases with shared items', false, 'Attributed purchases. Also accepts "purchases".', '32'),
      H('Purchases conversion value for shared items only', false, 'Attributed revenue (INR). Also accepts "revenue".', '95000.00'),
    ],
  },
  [K('Flipkart', 'Off Platform Ads', 'Criteo by FK', 'Consolidated Report')]: {
    description: 'Criteo dynamic-retargeting campaigns run BY Flipkart. Each campaign name carries an inline date range (…_May8_May31_…) that the parser auto-extracts. Files often have a "Date: …" title row at the very top — the parser detects it and promotes row 1 as the real header row.',
    source: 'Flipkart 3P Performance dashboard → Criteo by FK → Export CSV',
    headers: [
      H('Campaign', true, 'Campaign name — SHOULD contain the inline date range (Mon<day>_Mon<day>) for auto-date backfill.', '207061_3P-PPA_Wellversed_May8_May31_Wellcore_Retargeting'),
      H('Ad Set', false, 'Ad set inside the campaign. Also accepts "Adset".', 'Retargeting_ATC30'),
      H('Spend', true, 'Spend (INR, pre-GST).', '12000.00'),
      H('Del Reach', false, 'Delivered reach. Also accepts "Del. Reach" / "Reach".', '55000'),
      H('Del. Impression', false, 'Delivered impressions. Also accepts "Del Impression" / "Impressions".', '120000'),
      H('Del Clicks', false, 'Delivered clicks. Also accepts "Del. Clicks" / "Clicks".', '3400'),
      H('Del CTR', false, 'Delivered CTR — decimal fraction or percentage. Also accepts "Del. CTR" / "CTR".', '2.83%'),
      H('PPV', false, 'Product page views.', '1400'),
      H('ATC', false, 'Add-to-cart events.', '160'),
      H('Sales', false, 'Attributed sales (units / orders).', '48'),
      H('Revenue', false, 'Attributed revenue (INR).', '48000.00'),
      H('ROAS', false, 'Native ROAS. Also accepts "ROI". Auto-computed from Revenue / Spend if omitted.', '4.00'),
    ],
  },

  // ============== AMAZON · CONSOLIDATED REPORT (SP / SB / SD) ==============
  [K('Amazon', 'Amazon Ads', 'Sponsored Products', 'Consolidated Report')]: {
    description: 'Amazon Sponsored Products Advertised Product (or Targeting) Report. Every native column is preserved and surfaced on the Ad Properties drill.',
    source: 'Amazon Ads Console → Reports → Sponsored Products → Advertised Product (or Targeting) → Bulk Excel',
    headers: [
      H('Date', true, 'Row date (any common format — parser normalises to YYYY-MM-DD).', '2026-06-01'),
      H('Portfolio name', false, 'Amazon Ads portfolio the campaign belongs to — used for BRAND routing via AMAZON_BRAND_ALIASES. Blank rows fall back to brand="Other".', 'Wellcore'),
      H('Campaign Name', true, 'Campaign name.', 'Dynamite_Preworkout_All_15_Broad_KW'),
      H('Ad Group Name', false, 'Ad group inside the campaign.', 'AG_Broad_KW'),
      H('Advertised SKU', false, 'SKU under this ad group.', 'DYN-PWO-30S'),
      H('Advertised ASIN', false, 'ASIN under this ad group.', 'B0DXXX1234'),
      H('Targeting', false, 'Targeting expression / keyword / ASIN.', 'preworkout supplement'),
      H('Impressions', false, 'Impressions served.', '18500'),
      H('Clicks', false, 'Clicks.', '420'),
      H('Click-Through Rate (CTR)', false, 'CTR (auto if omitted).', '2.27%'),
      H('Spend', true, 'Spend (INR, pre-GST). Also accepts "Total Cost".', '7770.00'),
      H('7 Day Total Sales', false, '7-day attributed sales (INR). Also accepts "Sales".', '38500.00'),
      H('Total Advertising Cost of Sales (ACOS)', false, 'Auto if omitted.', '20.19%'),
      H('Total Return on Advertising Spend (ROAS)', false, 'Auto if omitted.', '4.95'),
      H('7 Day Total Orders (#)', false, 'Total orders. Also accepts "Orders" / "Purchases".', '18'),
      H('7 Day Total Units (#)', false, 'Total units. Also accepts "Units".', '22'),
    ],
  },
  [K('Amazon', 'Amazon Ads', 'Sponsored Brand', 'Consolidated Report')]: {
    description: 'Amazon Sponsored Brands campaign performance export. Supports NTB, video, brand-search metrics.',
    source: 'Amazon Ads Console → Reports → Sponsored Brands → Campaign → Bulk Excel',
    headers: [
      H('Date', true, 'Row date (any common format — parser normalises to YYYY-MM-DD).', '2026-06-01'),
      H('Portfolio name', false, 'Amazon Ads portfolio the campaign belongs to — used for BRAND routing.', 'Wellcore'),
      H('Campaign Name', true, 'Campaign name.', 'Wellcore_Whey_SB_Video'),
      H('Ad Group Name', false, 'Ad group inside the campaign.', 'Whey_Broad'),
      H('Impressions', false, 'Impressions.', '85000'),
      H('Clicks', false, 'Clicks.', '1200'),
      H('Spend', true, 'Spend (INR, pre-GST). Also accepts "Total Cost".', '18500.00'),
      H('14 Day Total Sales', false, '14-day attributed sales (INR). Also accepts "Sales".', '110000.00'),
      H('14 Day Total Orders (#)', false, 'Orders. Also accepts "Orders" / "Purchases".', '65'),
      H('14 Day Total Units (#)', false, 'Total units. Also accepts "Units".', '80'),
      H('Total Advertising Cost of Sales (ACOS)', false, 'Auto if omitted.', '16.82%'),
      H('Total Return on Advertising Spend (ROAS)', false, 'Auto if omitted.', '5.95'),
    ],
  },
  [K('Amazon', 'Amazon Ads', 'Sponsored Display', 'Consolidated Report')]: {
    description: 'Amazon Sponsored Display campaign performance export.',
    source: 'Amazon Ads Console → Reports → Sponsored Display → Campaign → Bulk Excel',
    headers: [
      H('Date', true, 'Row date.', '2026-06-01'),
      H('Portfolio name', false, 'Amazon Ads portfolio — used for brand routing.', 'Wellcore'),
      H('Campaign Name', true, 'Campaign name.', 'Dynamite_SD_Remarketing'),
      H('Impressions', false, 'Impressions. Parser also accepts "viewable Impressions".', '150000'),
      H('Clicks', false, 'Clicks.', '2100'),
      H('Spend', true, 'Spend (INR, pre-GST). Also accepts "Total Cost".', '9800.00'),
      H('14 Day Total Sales', false, 'Attributed sales (INR). Also accepts "Sales".', '54000.00'),
      H('14 Day Total Orders (#)', false, 'Orders. Also accepts "Orders" / "Purchases".', '25'),
      H('14 Day Total Units (#)', false, 'Units. Also accepts "Units".', '30'),
      H('Total Advertising Cost of Sales (ACOS)', false, 'Auto if omitted.', '18.15%'),
      H('Total Return on Advertising Spend (ROAS)', false, 'Auto if omitted.', '5.51'),
    ],
  },
  [K('Amazon', 'Amazon Ads', 'ONO DSP', 'Consolidated Report')]: {
    description: 'Amazon ONO DSP (Owned & Operated Display Side Platform) cumulative report — Insertion Order + Line Item level. File has no per-day breakdown (only Starts/Ends spanning the full IO window), so the user picks a Report Month and the parser spreads every row evenly across every day of that month. Brand is inferred from the Insertion Order name (Wellcore / Osmo / YouWeFit / etc.).',
    source: 'Amazon DSP Console → Reports → Insertion Order / Line Item Performance → Excel export',
    headers: [
      H('Account name', false, 'Advertiser account name (e.g. "IN - GCS - CPG - Wellcore").', 'IN - GCS - CPG - Wellcore'),
      H('Account ID', false, 'Advertiser account ID.', '586658489959901563'),
      H('Marketplace', false, 'Marketplace region.', 'India'),
      H('Insertion order', false, 'Insertion Order name — used to infer BRAND (Wellcore / Osmo / YouWeFit / Dynamite / Okami / Zero Sugar).', ''),
      H('Insertion order ID', false, 'Insertion Order ID.', ''),
      H('Order ID', true, 'Order ID (the "Insertion Order" identifier used to group Line items).', '577869092998693103'),
      H('Order', true, 'Insertion-Order display name — used as campaign_name and as source for BRAND detection.', 'IN - GCS - CPG - Wellcore_2.25L_BAU_DSP_'),
      H('Line item ID', true, 'Line item ID.', '591255661917626734'),
      H('Line item', true, 'Line item name — used as ad_group.', 'Remarketing - Display | 56,250'),
      H('Starts', false, 'IO start date (informational; parser uses Report Month instead).', '2026-04-01'),
      H('Ends', false, 'IO end date (informational).', '2026-09-30'),
      H('Delivery to budget', false, 'Pacing metric (0-1).', '0.16'),
      H('Budget', false, 'IO budget (INR).', '112500'),
      H('Budget (Impressions)', false, 'Impression budget (rare).', ''),
      H('Total cost', true, 'Spend (INR, pre-GST). MAPPED TO ad_spend.', '18057.00'),
      H('Impressions', false, 'Impressions delivered.', '129458'),
      H('eCPM', false, 'Effective CPM (informational).', '139.00'),
      H('Total eCPP', false, 'Effective cost per purchase (informational).', '56.00'),
      H('Total units sold', false, 'Units sold.', '327'),
      H('Total purchases', false, 'Purchases.', '325'),
      H('Total purchase rate', false, 'Purchase rate (fraction).', '0.00251'),
      H('Total sales', true, 'Revenue (INR). MAPPED TO total_revenue.', '204427.00'),
      H('Total ROAS', false, 'Native pre-GST ROAS. Preserved verbatim on every distributed day.', '11.32'),
      H('Total new-to-brand product sales', false, 'NTB sales (informational).', '161051.00'),
      H('Total % of purchases new-to-brand', false, 'NTB purchase share.', '0.806154'),
      H('Total new-to-brand ROAS', false, 'NTB ROAS (informational).', '8.92'),
    ],
  },
  [K('Amazon', 'Off Platform Ads', '3P DSP', 'Consolidated Report')]: {
    description: 'Amazon 3P DSP cumulative report — user picks a date range at upload; spend + revenue are distributed evenly across every day in the range. Full source file has 117 columns; parser only reads the 8 below.',
    source: 'DSP console → Reports → Campaign Performance → CSV export',
    headers: [
      H('Campaign Name', true, 'Campaign name.', 'Dynamite_Extreme_30_Conversion_Jay'),
      H('Impressions', false, 'Impressions. Parser also accepts "viewable Impressions".', '5200000'),
      H('Clicks', false, 'Clicks.', '18500'),
      H('Spend', true, 'Spend (INR, pre-GST). Parser also accepts "cost" or "spends".', '450000.00'),
      H('Total Sales', false, 'Attributed sales (INR). Also accepts "sales" / "revenue".', '2400000.00'),
      H('ROAS', false, 'Native pre-GST ROAS. Auto-computed from Total Sales / Spend if omitted.', '5.33'),
      H('ACOS %', false, 'Native ACOS %. Auto-computed if omitted.', '18.75%'),
      H('Purchases units', false, 'Total units sold. Also accepts "units".', '1250'),
    ],
  },

  // ============== ZEPTO · SP / SB / SD ==============
  [K('Zepto', 'Zepto Ads', 'Sponsored Products', 'Consolidated Report')]: {
    description: 'Zepto Sponsored Products campaign-level export. File is cumulative (no date column) — pick Start/End date at upload; spend + revenue are distributed evenly across every day.',
    source: 'Zepto Ads Manager → Reports → Sponsored Products → Export CSV',
    headers: [
      H('CampaignName', true, 'Campaign name.', 'Dynamite_Preworkout_VO_FB_15S_CROSS_SP'),
      H('BrandID', false, 'Internal brand ID.', '12'),
      H('BrandName', false, 'Brand name — used as the row brand.', 'Dynamite'),
      H('CampaignType', false, 'Zepto campaign type.', 'MANUAL'),
      H('Impressions', false, 'Impressions.', '25000'),
      H('Clicks', false, 'Clicks.', '450'),
      H('Spend', true, 'Spend (INR, pre-GST).', '3200.00'),
      H('Revenue', false, 'Attributed revenue (INR).', '20200.00'),
      H('Roas', false, 'Native pre-GST ROAS — preserved verbatim on every daily row.', '6.31'),
      H('Robas', false, 'Return on brand ad spend.', '4.85'),
      H('Orders', false, 'Total orders.', '52'),
      H('Atc', false, 'Add-to-cart events.', '420'),
      H('Same_skus', false, 'Same-SKU attribution.', '18'),
      H('Other_skus', false, 'Cross-SKU attribution.', '5'),
      H('Cpc', false, 'Cost per click (weighted totals recomputed on aggregate).', '7.11'),
      H('Cpm', false, 'Cost per mille.', '128.00'),
      H('Daily_budget', false, 'Campaign daily budget cap.', '1500'),
      H('Status', false, 'Campaign status.', 'ACTIVE'),
    ],
  },
  [K('Zepto', 'Zepto Ads', 'Sponsored Brand', 'Consolidated Report')]: {
    description: 'Zepto Sponsored Brand campaign-level export. Same schema as Sponsored Products (no CPC column).',
    source: 'Zepto Ads Manager → Reports → Sponsored Brand → Export CSV',
    headers: [
      H('CampaignName', true, 'Campaign name.', 'Dynamite_Preworkout_VO_FB_15S_KW_SB'),
      H('BrandName', false, 'Brand name.', 'Dynamite'),
      H('CampaignType', false, 'Zepto campaign type.', 'MANUAL'),
      H('Impressions', false, 'Impressions.', '85000'),
      H('Clicks', false, 'Clicks.', '1200'),
      H('Spend', true, 'Spend (INR, pre-GST).', '8500.00'),
      H('Revenue', false, 'Attributed revenue (INR).', '146500.00'),
      H('Roas', false, 'Native pre-GST ROAS.', '17.24'),
      H('Robas', false, 'Return on brand ad spend.', '11.20'),
      H('Orders', false, 'Total orders.', '210'),
      H('Atc', false, 'Add-to-cart events.', '1800'),
      H('Cpm', false, 'Cost per mille.', '100.00'),
      H('Daily_budget', false, 'Campaign daily budget cap.', '2000'),
      H('Status', false, 'Campaign status.', 'ACTIVE'),
    ],
  },

  // ============== BLINKIT · PERFORMANCE / REACH ==============
  [K('Blinkit', 'On-Platform Ads — Performance', 'Product Booster', 'Consolidated Report')]: {
    description: 'Blinkit multi-sheet Ads Manager export. Upload the full workbook via the "Product Booster" entry — the parser iterates every sheet in the file (Product Booster / Recommendation Ads / Product Shelf → Performance; Stories / Prime Banner / Brand Spotlight / Listing Spotlight / Brand Booster → Reach) and routes each row to its correct ad-type/ad-product. These are the columns from the Product Booster sheet.',
    source: 'Blinkit Ads Manager → Reports → Full workbook (all sheets in one XLSX)',
    headers: [
      H('Date', true, 'Row date (DD-MM-YYYY or YYYY-MM-DD). Parser handles both.', '01-06-2026'),
      H('Campaign ID', false, 'Blinkit campaign ID.', 'CMP_5821'),
      H('Campaign Name', true, 'Campaign name (first token becomes the brand).', 'Dynamite_Preworkout_VO_CATB_15S_ProductBooster_Gen_KW'),
      H('Targeting Type', false, 'Auto / Manual / Broad / Exact / Product.', 'Broad'),
      H('Targeting Value', false, 'Targeting expression (keyword / ASIN / audience).', 'preworkout'),
      H('Match Type', false, 'Match type for keyword targeting.', 'Broad'),
      H('Pacing Type', false, 'Standard / Accelerated.', 'Standard'),
      H('Impressions', false, 'Impressions.', '18500'),
      H('Unique Clicks', false, 'Unique clicks.', '340'),
      H('Estimated Budget Consumed', true, 'Spend (INR, pre-GST).', '3200.00'),
      H('Direct ATC', false, 'Direct add-to-carts.', '120'),
      H('Indirect ATC', false, 'Indirect add-to-carts.', '45'),
      H('Direct Quantities Sold', false, 'Direct units.', '52'),
      H('Indirect Quantities Sold', false, 'Indirect units.', '18'),
      H('Direct Sales', false, 'Direct sales (INR).', '18500.00'),
      H('Indirect Sales', false, 'Indirect sales (INR).', '4200.00'),
      H('Direct RoAS', false, 'Native direct ROAS.', '5.78'),
      H('Total RoAS', false, 'Native total ROAS — preserved verbatim.', '7.09'),
      H('New Users Acquired', false, 'NTB user count.', '38'),
      H('CPM', false, 'Cost per mille.', '172.97'),
      H('Most Viewed Position', false, 'Best position achieved (Product Booster only).', '3'),
    ],
  },
  [K('Blinkit', 'On-Platform Ads — Reach', 'Prime Banner', 'Consolidated Report')]: {
    description: 'Blinkit Prime Banner sheet from the multi-sheet Ads Manager export. Reach-track sheets have Reach / CTR / Unique Clicks (no conversion columns).',
    source: 'Blinkit Ads Manager → Reports → Full workbook → Prime Banner sheet',
    headers: [
      H('Date', true, 'Row date (DD-MM-YYYY or YYYY-MM-DD).', '01-06-2026'),
      H('Campaign ID', false, 'Blinkit campaign ID.', 'CMP_7014'),
      H('Campaign Name', true, 'Campaign name.', "Dynamite - Prime banner (May'26)"),
      H('Pacing Type', false, 'Standard / Accelerated.', 'Standard'),
      H('Impressions', false, 'Impressions.', '95000'),
      H('Reach', false, 'Unique users reached.', '32000'),
      H('Unique Clicks', false, 'Unique clicks.', '1800'),
      H('CTR', false, 'CTR (auto if omitted).', '1.89%'),
      H('CPM', false, 'Cost per mille.', '210.00'),
      H('Estimated Budget Consumed', true, 'Spend (INR, pre-GST).', '19950.00'),
    ],
  },

  // ============== D2C · META ADS (Instagram / Facebook / Threads) ==============
  [K('D2C', 'Meta Ads', 'Instagram', 'Consolidated Report')]: {
    description: 'Facebook Ads Manager "by Platform" export. One file auto-routes ALL platform rows (Instagram / Facebook / Threads → Meta Ads · Audience Network / Messenger / WhatsApp → folded into Facebook per user directive). Brand is derived from Account name first token (Wellcore / YouWeFit / Dynamite / Osmo / Okami); multi-brand accounts like "Brands Wellversed" or "WV Internal" fall back to campaign-name.',
    source: 'Meta Ads Manager → Reports → Custom → Breakdown by Platform → Export CSV',
    headers: [
      H('Account name', true, 'Ad account name (first token → brand).', 'Wellcore Wellversed'),
      H('Day', true, 'Row date. Parser also accepts "Date".', '2026-06-01'),
      H('Platform', true, 'facebook / instagram / threads / whatsapp / audience_network / messenger. Blank / unknown → folded into Facebook.', 'instagram'),
      H('Placement', false, 'Meta placement (feed / stories / reels …) — stored as ad_group.', 'instagram_stories'),
      H('Campaign name', true, 'Campaign name. Parser also accepts "Campaign".', 'DVC_Conversion_Campaign'),
      H('Reach', false, 'Unique users reached.', '18500'),
      H('Impressions', false, 'Impressions. Parser also accepts "Impr.".', '45000'),
      H('Frequency', false, 'Avg frequency.', '2.43'),
      H('Amount spent (INR)', true, 'Spend (INR, pre-GST). Also accepts "Amount spent" / "Cost" / "Spend".', '3200.00'),
      H('Attribution setting', false, 'Attribution window (metadata only).', '7-day click, 1-day view'),
      H('Revenue', false, 'Attributed revenue (INR). Also accepts "Purchase value" / "Purchases conversion value".', '9500.00'),
      H('ROAS', false, 'Native ROAS — preserved as pre-GST value. Also accepts "Purchase ROAS" / "Website purchase ROAS (return on ad spend)".', '2.97'),
      H('Link clicks [On ad]', false, 'Link clicks. Also accepts "Link clicks" / "Clicks (all)" / "Clicks".', '850'),
      H('CTR (all)', false, 'CTR. Auto-recomputed from clicks/impressions if omitted.', '1.89%'),
      H('CPM (cost per 1,000 impressions)', false, 'Native CPM. Also accepts "CPM".', '71.11'),
    ],
  },
  // Facebook / Threads / WhatsApp all use the SAME source file — reference the Instagram spec.
  [K('D2C', 'Meta Ads', 'Facebook', 'Consolidated Report')]: 'ALIAS::D2C::Meta Ads::Instagram::Consolidated Report',
  [K('D2C', 'Meta Ads', 'Threads', 'Consolidated Report')]: 'ALIAS::D2C::Meta Ads::Instagram::Consolidated Report',
  [K('D2C', 'WhatsApp', 'WhatsApp', 'Consolidated Report')]: 'ALIAS::D2C::Meta Ads::Instagram::Consolidated Report',

  // ============== D2C · GOOGLE ADS (YouTube / Search Network) ==============
  [K('D2C', 'Google Ads', 'YouTube', 'Consolidated Report')]: {
    description: 'Google Ads daily performance export. Per-row routing is done off the CAMPAIGN name: campaign name contains "youtube" or "demand" → YouTube; everything else (Search / PMax / Shopping) → Search Network. One file populates both products. Brand is the first token of Campaign (Wellcore / YouWeFit / Dynamite / Osmo / Wellversed) normalised through the BRAND_ALIASES priority list.',
    source: 'Google Ads → Reports → Predefined → Campaign → Segment by Day → Download CSV',
    headers: [
      H('Day', true, 'Row date. Parser also accepts "Date".', '2026-06-01'),
      H('Campaign', true, 'Campaign name. Routing driver — must contain "youtube" or "demand" to route to YouTube; otherwise routes to Search Network.', 'Dynamite_Pre-Workout_YouTube_ToF_Influencer'),
      H('Impr.', false, 'Impressions. Parser also accepts "Impressions".', '52000'),
      H('Clicks', false, 'Clicks.', '850'),
      H('CTR', false, 'CTR (auto-recomputed from Clicks / Impr. if omitted or expressed as a decimal fraction).', '1.63%'),
      H('Avg. CPC', false, 'Native average CPC. Also accepts "CPC".', '5.29'),
      H('Cost', true, 'Spend (INR, pre-GST). Also accepts "Spend".', '4500.00'),
      H('Conv. value', false, 'Attributed revenue (INR). Also accepts "Conv value" / "Conversion value".', '9600.00'),
      H('Conversions (by conv. time)', false, 'Converted units / orders. Also accepts "Conversions".', '12'),
      H('TrueView views', false, 'YouTube TrueView views (video creatives only). Also accepts "Views".', '2200'),
    ],
  },
  [K('D2C', 'Google Ads', 'Search Network', 'Consolidated Report')]: 'ALIAS::D2C::Google Ads::YouTube::Consolidated Report',

  // ============== D2C · WHATSAPP · AUTOMATION ==============
  [K('D2C', 'WhatsApp', 'Automation', 'Consolidated Report')]: {
    description: 'D2C WhatsApp Automation wide-monthly export. Each campaign row spans repeating 9-column blocks per month. The parser detects month labels from the row above the metric-header row and distributes monthly totals evenly across every day of that month.',
    source: 'WhatsApp Automation dashboard → Reports → Monthly Wide Export',
    headers: [
      H('<Group Name>', true, 'Group name in column A (defines the brand/group anchor for rows below).', 'Wellcore'),
      H('Campaign Name', true, 'Column B header — campaign name on data rows.', 'Wellcore Diwali Blast'),
      H('Content', false, 'Column C header — template/content name.', 'Diwali_Template_1'),
      H('<Month Label>', true, 'Month header above each 9-col block (repeats per month).', "October'25"),
      H('ROAS', true, 'First metric column in each month block.', '1.5'),
      H('Total Sent', false, 'Messages sent that month.', '100000'),
      H('Delivered', false, 'Messages delivered.', '90000'),
      H('Seen', false, 'Messages seen.', '80000'),
      H('Open Rate', false, 'Open rate (%).', '5.0'),
      H('Click Rate', false, 'Click rate (%).', '10.0'),
      H('Total Clicks', false, 'Clicks.', '8000'),
      H('Revenue', false, 'Monthly revenue (INR) — distributed evenly across days.', '15000.00'),
      H('Net Spends', true, 'Monthly spend (INR) — distributed evenly across days.', '10000.00'),
    ],
  },
  [K('D2C', 'WhatsApp', 'Manual', 'Consolidated Report')]: {
    description: 'D2C WhatsApp Manual sends. Parser not yet wired — share a sample file to enable this upload.',
    source: 'WhatsApp Manual sends dashboard → export CSV',
    headers: [],
  },

  // ============== SEARCH TRENDS ==============
  [K('', '', '', 'Search Trends Upload')]: {
    description: 'Bulk upload of monthly search-volume data across keywords per (category × channel).',
    source: 'Any keyword-tracking tool (Semrush, Ahrefs, Google Trends export) — normalise columns to match this format.',
    headers: [
      H('category', true, 'Search trends category (auto-added if new).', 'Creatine'),
      H('channel', true, 'Amazon / Flipkart / Google / OSC / etc.', 'Amazon'),
      H('month', true, 'MM-YYYY or YYYY-MM (parser normalises).', '06-2026'),
      H('keyword', true, 'Search keyword.', 'creatine monohydrate 250g'),
      H('search_volume', true, 'Monthly search volume (integer).', '18500'),
    ],
  },
};

// Resolve ALIAS entries so callers get a real spec object.
function resolve(specOrAlias) {
  if (typeof specOrAlias === 'string' && specOrAlias.startsWith('ALIAS::')) {
    return SPECS[specOrAlias.slice('ALIAS::'.length)] || null;
  }
  return specOrAlias || null;
}

/**
 * Look up the header spec for a specific selection.
 * @param {{channel:string, adType:string, adProduct:string, reportType:string}} sel
 * @returns {object|null}
 */
export function getReportHeadersSpec({ channel, adType, adProduct, reportType }) {
  if (!reportType) return null;
  // 1. Exact match
  const exact = SPECS[K(channel, adType, adProduct, reportType)];
  if (exact) return resolve(exact);
  // 2. Fallback on adProduct::reportType (search across channels)
  for (const key of Object.keys(SPECS)) {
    const parts = key.split('::');
    if (parts[2] === (adProduct || '').trim() && parts[3] === (reportType || '').trim()) {
      return resolve(SPECS[key]);
    }
  }
  return null;
}

/**
 * Serialize the spec's headers into a CSV string (single header row) for
 * the "Download Headers Only" quick-export button.
 */
export function serializeHeadersCsv(spec) {
  if (!spec || !Array.isArray(spec.headers) || spec.headers.length === 0) return '';
  const escape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return spec.headers.map(h => escape(h.name)).join(',') + '\n';
}
